import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/aventures";
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(mode === "login" ? "Bienvenue !" : "Compte créé");
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    sessionStorage.setItem("tao_post_login", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/aventures";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-8 py-14 md:py-20">
      <div className="rounded-2xl bg-white border border-[#E8DCC4] p-6 md:p-8 shadow-sm">
        <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold text-center">
          Votre aventure
        </p>
        <h1 className="font-serif text-3xl text-[#1F2A24] text-center mt-2">
          {mode === "login" ? "Se connecter" : "Créer mon compte"}
        </h1>
        <div className="gold-divider w-16 mx-auto my-5" />

        <button
          type="button"
          data-testid="google-login"
          onClick={googleLogin}
          className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-[#E8DCC4] bg-white px-5 py-3 font-ui text-sm text-[#1F2A24] hover:bg-[#F8F5EE] transition-colors"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continuer avec Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-[#E8DCC4] flex-1" />
          <span className="font-sans text-xs text-[#1F2A24]/40">ou</span>
          <div className="h-px bg-[#E8DCC4] flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input
              data-testid="register-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Votre prénom"
              className="w-full rounded-full border border-[#E8DCC4] px-4 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
            />
          )}
          <input
            data-testid="auth-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.fr"
            className="w-full rounded-full border border-[#E8DCC4] px-4 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
          />
          <input
            data-testid="auth-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="w-full rounded-full border border-[#E8DCC4] px-4 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
          />
          {error && (
            <p data-testid="auth-error" className="font-sans text-sm text-[#A63B36]">{error}</p>
          )}
          <button
            type="submit"
            data-testid="auth-submit"
            disabled={busy}
            className="w-full rounded-full bg-[#385E3A] text-white px-5 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors disabled:opacity-50"
          >
            {busy ? "…" : mode === "login" ? "SE CONNECTER" : "CRÉER MON COMPTE"}
          </button>
        </form>

        <p className="font-sans text-sm text-[#1F2A24]/70 text-center mt-5">
          {mode === "login" ? (
            <>Pas encore de compte ?{" "}
              <button data-testid="switch-register" onClick={() => setMode("register")} className="text-[#385E3A] font-semibold underline">
                Créer mon compte
              </button>
            </>
          ) : (
            <>Déjà un compte ?{" "}
              <button data-testid="switch-login" onClick={() => setMode("login")} className="text-[#385E3A] font-semibold underline">
                Se connecter
              </button>
            </>
          )}
        </p>
      </div>
      <p className="text-center mt-6">
        <Link to="/aventures" className="font-sans text-sm text-[#1F2A24]/50 hover:text-[#385E3A]">← Retour aux aventures</Link>
      </p>
    </div>
  );
}
